import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { Shader } from './webgl/constants'
import {
    createProgram,
    createTexture,
    createBuffer,
    renderPanorama,
    requestAnimationFrame,
    cancelAnimationFrame,
    removeProgram,
    resizeViewPort,
    isVideoPlaying,
} from './webgl'

export const VIDEO_STATE = {
    PLAYING: 'playing',
    PAUSED: 'paused',
}

const CursorState = {
    GRAB: 'grab',
    GRABBING: 'grabbing',
}

class PanoPlayer extends PureComponent {
    gl
    buffer
    texture
    program
    vertexPosition
    dragging = false
    isUserInteracting = false
    xSpeed = 0
    ySpeed = 0
    currentDragX = 0
    currentDragY = 0
    startDragX = 0
    startDragY = 0
    rafID
    sourceURL
    unmounting = false
    isVideoPaused = false

    constructor(props) {
        super(props)

        this.canvasRef = React.createRef()
        this.state = { cursor: CursorState.GRAB }
    }

    init = () => {
        try {
            this.gl =
                this.canvasRef.current.getContext('webgl') || this.canvasRef.current.getContext('experimental-webgl')
        } catch (error) {}

        if (!this.gl) {
            throw new Error('cannot create webgl context')
        }

        this.buffer = createBuffer(this.gl)
        this.animate()
    }

    renderTexture = sourceURL => {
        this.program = undefined
        this.texture = createTexture(this.gl, sourceURL, this.onTextureLoaded)
    }

    onTextureLoaded = () => {
        if (this.unmounting) return false

        const { onTextureLoaded } = this.props

        this.program = createProgram(this.gl, Shader.vertexShader, Shader.fragmentShader)

        onTextureLoaded && onTextureLoaded()
    }

    updateVideoState = (video, { muted, volume, videoState }) => {
        video.muted = muted
        video.volume = volume

        switch (videoState) {
            case VIDEO_STATE.PLAYING:
                video.play()
                this.isVideoPaused = false
                break
            case VIDEO_STATE.PAUSED:
                video.pause()
                this.isVideoPaused = true
                break
            default:
                break
        }
    }

    updateProps = ({ sourceURL, yaw, pitch, width, height, fov, autoRotate, rotationSpeed, enableInteractions }) => {
        this.yaw = autoRotate && this.yaw ? this.yaw : yaw // if rotating continue to use newly calculated yaw
        this.pitch = pitch
        this.width = width
        this.height = height
        this.fov = fov
        this.autoRotate = autoRotate
        this.rotationSpeed = rotationSpeed
        this.enableInteractions = enableInteractions
        this.sourceURL = sourceURL
    }

    uninit = () => {
        const { onUnMount } = this.props
        cancelAnimationFrame(this.rafID)

        const { gl, texture, buffer, program } = this

        removeProgram({
            gl,
            texture,
            buffer,
            program,
        })

        onUnMount && onUnMount()
    }

    animate = () => {
        const {
            gl,
            program,
            buffer,
            texture,
            vertexPosition,
            yaw,
            pitch,
            width,
            height,
            fov,
            autoRotate,
            isVideoPaused,
            props: { clickAndDrag },
        } = this

        if (autoRotate && !isVideoPaused) this.yaw -= this.rotationSpeed

        renderPanorama({
            gl,
            program,
            buffer,
            texture,
            vertexPosition,
            width,
            height,
            yaw: clickAndDrag ? -yaw : yaw,
            pitch: clickAndDrag ? pitch : -pitch,
            fov,
        })

        if (this.enableInteractions) this.handleMouse()

        this.rafID = requestAnimationFrame(this.animate)
    }

    handleMouse = () => {
        if (this.isUserInteracting) {
            this.dragPano()
        } else {
            this.stopPanoDrag()
        }
    }

    handleMouseDown = event => {
        const clientX = (event.touches && event.touches[0].clientX) || event.clientX
        const clientY = (event.touches && event.touches[0].clientY) || event.clientY

        this.startDragX = clientX
        this.startDragY = clientY

        this.currentDragX = clientX
        this.currentDragY = clientY

        this.isUserInteracting = true

        const { onDragStart } = this.props
        onDragStart && onDragStart(event)
        this.setState({ cursor: CursorState.GRABBING })

        return false
    }

    handleMouseUp = event => {
        if (this.dragging) {
            const { onDragEnd } = this.props

            onDragEnd && onDragEnd(event)
        }

        this.dragging = false
        this.isUserInteracting = false
        this.setState({ cursor: CursorState.GRAB })

        return false
    }

    handleMouseMove = event => {
        if (this.isUserInteracting) {
            this.dragging = true
            this.autoRotate = false

            const clientX = (event.touches && event.touches[0].clientX) || event.clientX
            const clientY = (event.touches && event.touches[0].clientY) || event.clientY

            this.currentDragX = clientX
            this.currentDragY = clientY

            const { clickAndDrag } = this.props

            if (clickAndDrag) {
                this.resetStartingDrag()
                this.dragPano()
            }
        }
        return false
    }

    resetStartingDrag = () => {
        clearTimeout(this.timeOut)

        this.timeOut = setTimeout(() => {
            this.startDragX = this.currentDragX
            this.startDragY = this.currentDragY
        }, 50)
    }

    dragPano = () => {
        this.xSpeed = (this.currentDragX - this.startDragX) / 200
        this.ySpeed = (this.currentDragY - this.startDragY) / 200

        this.yaw += this.xSpeed
        this.pitch += this.ySpeed
    }

    stopPanoDrag = () => {
        this.xSpeed *= 0.8
        this.ySpeed *= 0.8

        this.yaw += this.xSpeed
        this.pitch += this.ySpeed
    }

    registerVideoEvents = video => {
        const {
            onAbort,
            onCanPlay,
            onDurationChange,
            onEmptied,
            onEnded,
            onError,
            onLoadedData,
            onLoadedMetaData,
            onLoadStart,
            onPause,
            onPlay,
            onPlaying,
            onProgress,
            onSeeked,
            onSeeking,
            onTimeUpdate,
            onVolumeChange,
            onWaiting,
        } = this.props

        video.onabort = onAbort
        video.oncanplay = onCanPlay
        video.ondurationchange = onDurationChange
        video.onemptied = onEmptied
        video.onended = onEnded
        video.onerror = onError
        video.onloadeddata = onLoadedData
        video.onloadedmetadata = onLoadedMetaData
        video.onloadstart = onLoadStart
        video.onpause = onPause
        video.onplay = onPlay
        video.onplaying = onPlaying
        video.onprogress = onProgress
        video.onseeked = onSeeked
        video.onseeking = onSeeking
        video.ontimeupdate = onTimeUpdate
        video.onvolumechange = onVolumeChange
        video.onwaiting = onWaiting
    }

    componentDidMount() {
        const { sourceURL } = this.props

        this.init(this.props)
        this.renderTexture(sourceURL)

        if (this.texture.video && !this.unmounting) {
            this.updateVideoState(this.texture.video, this.props)
            this.registerVideoEvents(this.texture.video)
        }

        this.updateProps(this.props)
    }

    // For compatibility with Aria-Viewer (react 15) this was renamed from UNSAFE_componentWillReceiveProps
    componentWillReceiveProps(newProps) {
        const { sourceURL, width, height } = newProps
        // Render new texture if neccessary
        if (this.sourceURL !== sourceURL) {
            this.texture.video && isVideoPlaying(this.texture.video) && this.texture.video.pause()
            this.renderTexture(sourceURL)
        }

        if (this.texture.video) {
            this.updateVideoState(this.texture.video, newProps)
            this.registerVideoEvents(this.texture.video)
        }

        // Handle resizing of viewport
        if (this.width !== width || this.height !== height) resizeViewPort(this.gl, width, height)

        this.updateProps(newProps)
    }

    componentWillUnmount() {
        this.unmounting = true
        this.uninit()
    }

    setCanvasRef = ref => {
        // Ref is being set this way to avoid this error when importing component:
        // https://reactjs.org/warnings/refs-must-have-owner.html
        this.canvasRef.current = ref
    }

    render() {
        const { width, height, children, enableInteractions } = this.props
        return (
            <div
                style={{
                    width: width + 'px',
                    height: height + 'px',
                    cursor: this.state.cursor,
                }}
            >
                <canvas
                    width={width}
                    height={height}
                    ref={this.setCanvasRef}
                    onMouseDown={enableInteractions ? this.handleMouseDown : undefined}
                    onTouchStart={enableInteractions ? this.handleMouseDown : undefined}
                    onMouseUp={enableInteractions ? this.handleMouseUp : undefined}
                    onTouchEnd={enableInteractions ? this.handleMouseUp : undefined}
                    onMouseMove={enableInteractions ? this.handleMouseMove : undefined}
                    onTouchMove={enableInteractions ? this.handleMouseMove : undefined}
                    onMouseLeave={enableInteractions ? this.handleMouseUp : undefined}
                />

                {children}
            </div>
        )
    }
}

PanoPlayer.propTypes = {
    yaw: PropTypes.number,
    pitch: PropTypes.number,
    fov: PropTypes.number,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    sourceURL: PropTypes.string.isRequired,
    autoRotate: PropTypes.bool,
    enableInteractions: PropTypes.bool,
    rotationSpeed: PropTypes.number,
    muted: PropTypes.bool,
    paused: PropTypes.bool,
    volume: PropTypes.number, // number between 0.0 (silent) and 1.0 (100%)
    videoState: PropTypes.oneOf([VIDEO_STATE.PLAYING, VIDEO_STATE.PAUSED]),
    clickAndDrag: PropTypes.bool,
}

PanoPlayer.defaultProps = {
    yaw: 0,
    pitch: 0,
    fov: 80,
    width: window.innerWidth,
    height: window.innerHeight,
    sourceURL: '',
    autoRotate: true,
    enableInteractions: true,
    rotationSpeed: 0.1,
    muted: false,
    volume: 1.0,
    videoState: VIDEO_STATE.PLAYING,
    clickAndDrag: true,
}

export default PanoPlayer
